import parsePath from './parse-path';

// constants
import { LocaleMap, Locales } from 'src/lib/constants';

export const isExternalUrl = (url: string): boolean => {
  if (url.startsWith('//')) return true;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Gets the last segment of a route ie: /products/transactions -> transactions
export const getCurrentPage = (route = ''): string => {
  const path = route.split('/').filter((segment) => {
    return segment !== '';
  });
  return path[path.length - 1];
};

export const isStaticAsset = (url: string): boolean => {
  return (
    url.match(
      /\.(js|map|woff2|ttf|svg|webmanifest|webm|mp4|jpg|jpeg|png|webp|css|pdf|zip|txt|json|xml|gif|bmp|exe|wav|tiff|rar|ram|gif|doc|avi|woff)/,
    )?.length > 0 || url.match(/apple-app-site-association/)?.length > 0
  );
};

export const appendTrailingSlash = (url = ''): string => {
  if (isStaticAsset(url)) return url;
  const { resource, hash, search } = parsePath(url);
  if (url === '#') return url;
  if (
    (resource && !resource.includes('plaid.com')) ||
    (resource && !/(\/\/|www.)plaid.com/.test(url))
  ) {
    return url;
  }
  if (search) return url.replace('?' + search, '/?' + search);
  if (hash) return url;
  if (!url.endsWith('/')) return `${url}/`;
  return url;
};

// The page's own path, with the query string and hash dropped so that
// `/products/identity/?utm_source=x#contact` and `/products/identity/` resolve
// to the same page identity.
export const getPagePath = (asPath = ''): string => {
  return appendTrailingSlash(parsePath(asPath).pathname);
};

// A page is its own canonical unless the CMS `canonicalUrl` field names another
// page, which editors set only to consolidate pages that duplicate each other.
export const getCanonicalUrl = ({
  canonicalPagePath,
  pagePath,
}: {
  canonicalPagePath?: string;
  pagePath: string;
}): string => {
  return `https://plaid.com${canonicalPagePath || pagePath}`;
};

export const formatUrl = (url: string): string => {
  try {
    const urlObj = new URL(url, 'https://plaid.com');
    return urlObj.href;
  } catch (e) {
    // this code remains for backwards compatiability until it's no longer needed
    if (url) {
      const startsWithHash = url.startsWith('#');
      if (startsWithHash) {
        return url;
      }
      const urlContainsHttp = url.includes('http');
      const urlContainsPeriod = url.includes('.');
      const startsWithSlash = url.startsWith('/');
      return startsWithSlash || !urlContainsPeriod || urlContainsHttp
        ? prependLeadingSlash(url)
        : `//${url}`;
    }
    return '';
  }
};

const HREF_REGEX = /href=("|')(.+?)("|')/g;

export const replaceHrefValueWithLocale = ({
  locale,
  str,
}: {
  locale: string;
  str: string;
}): string => {
  return str.replace(HREF_REGEX, (match, p1, p2) => {
    return `href=\"${prependLocale({ href: p2, locale })}\"`;
  });
};

export const mutateHardCodedLinks = <TData>({
  data,
  locale,
}: {
  data: TData;
  locale: string;
}): TData | null => {
  // handle null and undefined
  if (!data) {
    return null;
  }

  Object.entries(data).forEach(([k, v]) => {
    // handle strings
    if (typeof v === 'string') {
      if (HREF_REGEX.test(v)) {
        data[k] = replaceHrefValueWithLocale({ locale, str: v });
      }
    }
    // handle objects and arrays
    if (typeof v === 'object') {
      // recurse
      mutateHardCodedLinks({ data: v, locale });
    }
  });

  return data;
};

export const prependLeadingSlash = (url: string): string => {
  const containsHttp = url.includes('http');
  return containsHttp || url.startsWith('/') ? url : `/${url}`;
};

export const isAbsolutePlaidUrl = (url: string): boolean => {
  return new RegExp('(www.|//|demo.|blog.)plaid.com').test(url);
};

export const stripLeadingSlash = (str: string): string => {
  if (str.startsWith('/')) {
    return str.substring(1);
  }
  return str;
};

export const isAnchor = (href: string): boolean => {
  return href.includes('#');
};

export const isLegacyLocaleLink = (href: string): boolean => {
  const legacyLocalePattern = Object.keys(LocaleMap).join('|');
  const hasLegacyLocale = new RegExp(`\/(${legacyLocalePattern})\/`, 'g').test(
    href,
  );
  return hasLegacyLocale;
};

export const hasLocale = (href: string): boolean => {
  const hrefContainsLocale = Object.values(Locales).some((locale) => {
    return href.includes(locale);
  });
  return hrefContainsLocale;
};

export const prependLocale = ({
  locale,
  href = '',
}: {
  locale?: string;
  href?: string;
}): string => {
  if (!locale || locale === Locales.EN_US) return href;
  // make absolute plaid.com URLs relative
  if (isAbsolutePlaidUrl(href) && !isStaticAsset(href)) {
    const split = href.split('.com');
    return prependLocale({ locale, href: split[1] });
  }
  if (isExternalUrl(href)) return href;
  if (isStaticAsset(href)) return href;
  if (isLegacyLocaleLink(href)) return href;
  if (hasLocale(href)) return href;
  if (isAnchor(href)) {
    if (stripLeadingSlash(href).startsWith('#')) return href;
    return prependLeadingSlash(locale + href);
  }
  return `/${locale}${appendTrailingSlash(prependLeadingSlash(href))}`;
};

export const prependStagingHash = (href = '', basePath: string): string => {
  if (!basePath || !href || href.includes(basePath)) return href;
  if (isExternalUrl(href)) return href;
  if (isAnchor(href)) {
    if (href.startsWith('#')) return href;
  }
  const normalizedHref = href.startsWith('/') ? href : `/${href}`;
  return `${basePath}${normalizedHref}`;
};

export const makeLocalizedUrl = ({
  domain,
  userLocale,
  pageLocale,
  path,
  basePath,
}: {
  domain: string;
  userLocale?: string;
  pageLocale: string;
  path: string;
  basePath: string;
}): string => {
  const parsedPath = parsePath(path);
  const pageSegment = appendTrailingSlash(
    parsedPath.pathname.replace(`/${pageLocale}`, ''),
  );
  if (!userLocale || userLocale === Locales.EN_US) {
    return `${domain}${basePath ? `${basePath}` : ''}${pageSegment}`;
  }
  return `${domain}${
    basePath ? `${basePath}/` : '/'
  }${userLocale}${pageSegment}`;
};

export const getLocalizedFallbackUrl = (
  userLocale: string,
  basePath?: string,
): string => {
  if (userLocale === Locales.EN_US) {
    return `${basePath ? `/${basePath}` : ''}/`;
  }
  return `${basePath ? `${basePath}` : ''}/${userLocale}/`;
};

export const fetchLocalizedEquivalentPage = async ({
  asPath,
  userLocale,
  pageLocale,
  basePath,
}) => {
  const DOMAIN = window.location.origin;
  try {
    const fetchUrl = makeLocalizedUrl({
      domain: DOMAIN,
      path: asPath,
      userLocale,
      pageLocale,
      basePath: basePath,
    });
    const response = await fetch(fetchUrl);
    if (response.status === 200) {
      // if the localized equivalent exists then dispatch payload so the toast
      // links the user to the correct page
      return fetchUrl.replace(DOMAIN, '');
    } else {
      // if no localized equivalent exists then link the user to the homepage
      // for their locale
      return getLocalizedFallbackUrl(userLocale, basePath);
    }
  } catch (error) {
    // something went wrong, let's link them to the homepage for their locale
    return getLocalizedFallbackUrl(userLocale, basePath);
  }
};

export const isPdfUrl = (url: string): boolean => {
  return url.split('?')[0].endsWith('.pdf');
};

export const getUrlDomain = (url) => {
  const matches = url.match(/^https?:\/\/([^/?#]+)(?:[/?#]|$)/i);
  return matches && matches[1].replace('www.', '');
};

export const mapSearchParamsToObject = (entries) => {
  const result = {};
  for (const [key, value] of entries) {
    // each 'entry' is a [key, value] tupple
    result[key] = value;
  }
  return result;
};

/**
 * Helper function which appends `?form=MARKETO_FORM_ID` to Marketo form success URLs
 * so Qualified meeting scheduling (which is often triggered on success pages)
 * can detect which form was just submitted.
 */
export const appendSuccessParam = (url: string, marketoFormId: number) => {
  try {
    const urlWithFormParam = new URL(url, 'https://plaid.com');

    // Sometimes the "success" URL of a Marketo form is a direct link to a Contentful
    // asset. When this is the case, don't append the form param.
    if (
      ![
        'assets.ctfassets.net',
        'images.ctfassets.net',
        'downloads.ctfassets.net',
      ].includes(urlWithFormParam.host)
    ) {
      urlWithFormParam.searchParams.set('form', marketoFormId.toString());
    }

    return urlWithFormParam.toString();
  } catch (err) {
    // Bail out if a user manages to insert an invalid URL into the CMS
    // eslint-disable-next-line no-console
    console.error(err);
    return url;
  }
};
